import React, { useEffect, useRef, useState } from 'react'
import { portal } from '../portalApi.js'

// 재무포털과 같은 계정 체계(/api/auth)를 사용하는 통합 로그인 (포털 SSO + 구글 + 로컬 아이디)
const GOOGLE_CLIENT_ID = '807528708224-iqe1gm0tj0vbi31sc0o63fopg9hin5ju.apps.googleusercontent.com'

// 포털에서 돌아오지 못했을 때 보여 줄 말. 서버는 코드만 붙여 보내고(자세한 까닭은
// 서버 로그에 남는다) 사람이 읽을 문장은 여기서 고른다.
const SSO_오류 = {
  failed: '포털 로그인에 실패했습니다. 다시 시도해 주세요.',
  not_allowed: '이 계정은 아직 접근 권한이 없습니다. 관리자에게 문의해 주세요.',
  // 서버는 로그인을 시켰는데 바로 다음 요청이 로그아웃으로 오는 경우다. 우리 쪽에서
  // 더 할 수 있는 일이 없고, 무엇을 봐야 하는지만 알려 주면 대개 그 자리에서 풀린다.
  nocookie: '로그인은 됐지만 브라우저가 로그인 상태를 저장하지 못했습니다. '
    + '시크릿 모드·쿠키 차단 설정·확장 프로그램, 그리고 PC 날짜·시각이 맞는지 확인해 주세요.',
}

// 서버가 주소에 붙여 보낸 표식만 걷어낸 주소. 남겨 두면 새로고침마다 안내가 다시 뜨고,
// 로그인 뒤 돌아갈 곳에도 섞여 들어가 한 번 누를 때마다 주소가 길어진다.
export function 표식뺀주소() {
  const { pathname, search, hash } = window.location
  const 쿼리 = search.replace(/[?&](sso_error|sso)=[^&]*/g, '').replace(/^&/, '?')
  return pathname + (쿼리 === '?' ? '' : 쿼리) + hash
}

// 들어온 뒤에는 표식을 주소에서 치운다. 로그인에 성공한 쪽(App.jsx)도 이걸 부른다.
export function 표식치우기() {
  try { window.history.replaceState({}, '', 표식뺀주소()) } catch {}
}

// 로그인 뒤 돌아갈 곳. 보던 주소를 그대로 들고 간다(해시 경로까지) - 이게 없으면
// 특정 화면을 열려다 로그인한 사람이 늘 홈으로 떨어진다.
// 우리 앱 안의 경로만 허용한다. '//evil.com' 은 브라우저가 다른 host 로 읽는다.
function 돌아갈곳() {
  const 길 = 표식뺀주소()
  if (!길.startsWith('/') || 길.startsWith('//') || 길.startsWith('/\\')) return '/'
  return 길
}

// 우리 앱에서 먼저 로그인하면 포털은 그 사실을 모른다. 로그인 직후 포털에 잠깐 들러
// 세션을 열어 주고 곧바로 제자리로 돌아온다 - 그래야 포털에서 다시 로그인하지 않는다.
// 서버가 주소를 내려 줄 때만 간다(비밀키가 없으면 서버가 안 내려 준다).
function 포털에알리기(user) {
  if (!user?.ssoPushUrl) return false
  const 돌아올곳 = window.location.pathname + window.location.search
  window.location.replace(`${user.ssoPushUrl}?next=${encodeURIComponent(돌아올곳)}`)
  return true
}

export default function Login({ onLogin }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const googleRef = useRef(null)
  const [sso주소] = useState(() => `/api/auth/sso/login?next=${encodeURIComponent(돌아갈곳())}`)

  // 포털에서 돌아오면 주소에 표식이 붙어 온다. 한 번 보여 주고 주소에서 지운다 -
  // 안 지우면 새로고침할 때마다 다시 뜨고, 로그인에 성공한 뒤에도 주소에 남는다.
  //
  // `sso=ok` 는 서버가 세션을 냈다는 표식이다. **그런데도 이 로그인 화면이 떴다면**
  // 그 쿠키가 브라우저에 저장되지 않았다는 뜻이다 - 계정도 권한도 멀쩡한데 눌러도
  // 아무 일이 안 나는 그 자리이고, 짚어 주지 않으면 사람은 원인을 알 길이 없다.
  useEffect(() => {
    const 주소값 = new URLSearchParams(window.location.search)
    const 코드 = 주소값.get('sso_error') || (주소값.get('sso') === 'ok' ? 'nocookie' : '')
    if (!코드) return
    setErr(SSO_오류[코드] || SSO_오류.failed)
    표식치우기()
  }, [])

  useEffect(() => {
    // Google Identity Services 버튼
    const init = () => {
      if (!window.google?.accounts?.id || !googleRef.current) return
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (resp) => {
          try {
            const user = await portal.post('/api/auth/google/login', { credential: resp.credential })
            if (!포털에알리기(user)) onLogin(user)
          } catch (ex) { setErr(ex.message) }
        },
      })
      window.google.accounts.id.renderButton(googleRef.current, {
        theme: 'outline', size: 'large', width: 276, text: 'signin_with', locale: 'ko',
      })
    }
    if (window.google?.accounts?.id) { init(); return }
    const s = document.createElement('script')
    s.src = 'https://accounts.google.com/gsi/client'
    s.async = true
    s.onload = init
    document.head.appendChild(s)
  }, [onLogin])

  const submit = async (e) => {
    e.preventDefault()
    if (!username || !password || busy) return
    setBusy(true); setErr('')
    try {
      const user = await portal.post('/api/auth/local/login', { username, password })
      if (!포털에알리기(user)) onLogin(user)
    } catch (ex) {
      setErr(ex.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={submit}>
        <div className="login-title">재무경영팀 ERP</div>
        <div className="login-sub">회사 계정으로 로그인하세요</div>
        {/* 포털에 이미 로그인해 있으면 이 단추 하나로 들어온다. 반드시 주소 이동이다 -
            포털로 넘어갔다 돌아와야 해서 fetch 로는 안 된다. */}
        <a className="btn" href={sso주소} style={{ textAlign: 'center' }}>
          🔑 에르모어 포털 계정으로 로그인
        </a>
        <div className="login-divider muted">또는</div>
        <div ref={googleRef} style={{ display: 'flex', justifyContent: 'center' }} />
        <div className="login-divider muted">또는</div>
        <input placeholder="아이디" value={username}
          onChange={(e) => setUsername(e.target.value)} />
        <input placeholder="비밀번호" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)} />
        {err && <div className="login-err">{err}</div>}
        <button className="btn" type="submit" disabled={busy || !username || !password}>
          {busy ? '확인 중…' : '로그인'}
        </button>
        <div className="login-foot muted">주식회사 에르모어 · fa.ermore.ai</div>
      </form>
    </div>
  )
}
